import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { InfoTooltip } from '@/components/tooltips/InfoTooltip/InfoTooltip';
import { SelectField } from '../SelectField/SelectField';
import { SelectItem } from '@/components/ui/select';
import type { StylingProps } from '@builder4/shared/types/layout';
import NodePositionControlSettingsV2 from '@/components/inspector-fields/PositionControl/NodePositionControlSettingsV2';

interface PositionProps {
  isRootContainer: boolean;
  className: string;
  onClick?: () => void;
  selectValue?: 'relative' | 'absolute' | 'fixed' | 'sticky';
  onChangeCallback?: (value: StylingProps['position']) => void;
}

export const Position: React.FC<PositionProps> = ({
  className = '',
  onClick,
  selectValue = 'relative',
  onChangeCallback,
  isRootContainer,
}) => {
  const [value, setValue] = useState<StylingProps['position']>(selectValue);

  const handleClick = () => {
    if (onClick) onClick();
  };

  const handleChange = (newValue: StylingProps['position']) => {
    setValue(newValue);
    if (onChangeCallback) onChangeCallback(newValue);
  };

  useEffect(() => {
    if (selectValue) {
      setValue(selectValue);
    }
  }, [selectValue]);

  return (
    <div onClick={handleClick} className={clsx('flex w-full flex-col gap-3', className)}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <p className="text-button-sm font-medium text-neutral-500">Position</p>
          <InfoTooltip text="How this element sits on your page. Most widgets use 'Relative' (stays in flow with other content). Use 'Absolute' or 'Fixed' to place it anywhere with exact coordinates. 'Sticky' makes it stick to the screen when scrolling." />
        </div>
        <SelectField
          className="w-full max-w-[140px]"
          size="full"
          defaultValue={value}
          onValueChange={(val) => handleChange(val as StylingProps['position'])}
          placeholder=""
          text=""
          dataTestId="position-selection"
        >
          <SelectItem value="relative">Relative</SelectItem>
          <SelectItem value="absolute">Absolute</SelectItem>
          <SelectItem disabled={!isRootContainer} value="fixed">
            Fixed
          </SelectItem>
          <SelectItem value="sticky">Sticky</SelectItem>
        </SelectField>
      </div>
      {value !== 'relative' && <NodePositionControlSettingsV2 value={value} />}
    </div>
  );
};
